import React from 'react';
import { TextField as MuiTextField } from '@mui/material';
import IMask from 'imask';
import type { Text as Interface } from '../../design/components';


// WCAG compliant TextField component

export const Text: React.FC<Interface> = ({
  name, description, state, placeholder, hidden, inline, readonly, disabled, required,
  error, type = 'text', multiline, maxlength, minlength, pattern, autocomplete, mask
}) => {
  const [value, setValue] = state || ['', () => { }];

  const masked = React.useMemo(() => {
    if (mask && !((!!multiline && multiline > 1))) {
      return IMask.createMask({ mask });
    }
    return null;
  }, [mask, multiline]);

  // Inline mode: render just the text value
  if (inline) {
    return (
      <span style={{ display: hidden ? 'none' : undefined }}>
        {value || ''}
      </span>
    );
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const input = e.target.value;
    if (masked) {
      masked.resolve(input);
      setValue(masked.unmaskedValue);
    } else {
      setValue(input);
    }
  };

  const getDisplayValue = () => {
    if (masked && value) {
      masked.resolve(value);
      return masked.value;
    }
    return value;
  };

  const aria: Record<string, boolean | string> = {};
  if (required) aria['aria-required'] = true;
  if (readonly) aria['aria-readonly'] = true;
  if (disabled) aria['aria-disabled'] = true;

  if (error) {
    aria['aria-invalid'] = true;
    aria['aria-errormessage'] = `${name}-error`;
  }

  if (autocomplete && autocomplete !== 'off') {
    aria['aria-autocomplete'] = 'list';
  }

  return (
    <MuiTextField
      id={name}
      name={name}
      label={name}
      helperText={error || description}
      value={getDisplayValue()}
      placeholder={placeholder}
      onChange={onChange}
      error={!!error}
      disabled={disabled}
      required={required}
      type={type}
      multiline={!!multiline && multiline > 1}
      rows={multiline}
      fullWidth
      slotProps={{
        input: { readOnly: readonly, hidden: hidden },
        htmlInput: { name, maxLength: maxlength, minLength: minlength, pattern, autoComplete: autocomplete, ...aria },
      }}
      sx={{ display: hidden ? 'none' : undefined }}
    />
  );
};
